import {TFunction} from 'i18next';
import {useCallback, useState, useEffect} from 'react';
import {useTranslation} from 'react-i18next';

import {predefinedNotifier, PredefinedNotifierType} from 'components';
import {ApiError, ToastProps} from 'types';
import {buildNotifierErrorPropsCommon} from 'utils';

type Options<T> = {
    onSuccess?: <A>(data: T, args?: A) => void;
    onError?: (error: ApiError) => void;
    onFinally?: () => void;
    notificationType?: PredefinedNotifierType;
    loadingDefault?: boolean; // useful when need to load a data on component mount
    buildNotifierErrorProps?: (error: ApiError, t: TFunction) => ToastProps;
    buildNotifierWarningProps?: (data: T, t: TFunction) => ToastProps;
    isWarningChecker?: (data: T) => boolean;
}

/**
 * The hook is used to simplify work with the API received data.
 * It takes care of loading state, data received, error received, and others
 *
 * @param fetchData Function returns data.
 * @param options
 * @param options.onSuccess Success callback. Should be wrapped with useCallback hook.
 * @param options.onError Error callback. Should be wrapped with useCallback hook.
 */
export function useApiData<T, A extends any[] = []>(
    fetchData: (...args: A) => Promise<T>,
    {
        onSuccess,
        onError,
        onFinally,
        loadingDefault = false,
        notificationType,
        buildNotifierErrorProps = buildNotifierErrorPropsCommon,
        isWarningChecker,
        buildNotifierWarningProps
    }: Options<T> = {}
) {
    const {t} = useTranslation();
    const [firstDataLoaded, setFirstDataLoaded] = useState(false);
    const [loading, setLoading] = useState(loadingDefault);
    const [loadedAt, setLoadedAt] = useState<Date>();
    const [error, setError] = useState<ApiError>();
    const [data, setData] = useState<T>();
    const [initialData, setInitialData] = useState<T>();

    const getNotifier = useCallback(() => predefinedNotifier[notificationType], [notificationType]);

    const send = useCallback(async (...args: A) => {
        const loadingNotifier = getNotifier()?.(t);
        setLoading(true);
        setError(undefined);
        setLoadedAt(undefined);
        try {
            const response = await fetchData(...args);
            setData(response);
            onSuccess?.(response, args);
            if (isWarningChecker?.(response)) {
                const warningProps = buildNotifierWarningProps?.(response, t);
                loadingNotifier?.onWarning(warningProps);
            } else {
                loadingNotifier?.onSuccess();
            }
            setLoadedAt(new Date());
        } catch (e: any) {
            setError(e);
            onError?.(e);
            let errorProps = buildNotifierErrorProps(e, t);
            if (errorProps === undefined) {
                errorProps = buildNotifierErrorPropsCommon(e, t);
            }
            loadingNotifier?.onError(errorProps);
        } finally {
            setLoading(false);
            setFirstDataLoaded(true);
            onFinally?.();
        }
    }, [getNotifier, t, fetchData, onSuccess, onError, buildNotifierErrorProps, onFinally, isWarningChecker, buildNotifierWarningProps]);

    const reset = useCallback(() => {
        setFirstDataLoaded(false);
        setLoading(false);
        setError(undefined);
        setInitialData(undefined);
        setData(undefined);
        setLoadedAt(undefined);
    }, []);

    useEffect(() => {
        if (data !== undefined && initialData === undefined) {
            setInitialData(data);
        }
    }, [firstDataLoaded, data, initialData]);

    return {send, data, initialData, loading, initialLoading: loading && !firstDataLoaded, error, loadedAt, reset};
}
